import { createBrowserRouter, createMemoryRouter, type RouteObject } from 'react-router'

import { PageLoader } from '@/components/common/page-loader'
import { RouteErrorBoundary } from '@/components/errors/route-error-boundary'
import { AppLayout } from '@/components/layout/app-layout'
import { HomeRedirect, RequireAuth, RequireUsersAccess } from '@/modules/auth'

import { paths, type RouteHandle } from './navigation'

/**
 * Route table. One shared shell for every persona with Projects, Users (admins only), Notifications and Settings. Pages
 * check the signed-in user's scope themselves (a project URL outside it is refused, not just hidden), and the commands
 * check it again. Each page sets `handle.title`, which the app bar displays.
 */
export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorBoundary />,
    hydrateFallbackElement: <PageLoader />,
    children: [
      {
        path: paths.login,
        lazy: () => import('@/modules/auth/pages/login-page').then((m) => ({ Component: m.LoginPage })),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            Component: AppLayout,
            children: [
              {
                path: paths.projects,
                handle: { title: 'Projects' } satisfies RouteHandle,
                lazy: () => import('@/modules/projects/pages/projects-page').then((m) => ({ Component: m.ProjectsPage })),
              },
              {
                path: paths.createProject,
                handle: { title: 'New project' } satisfies RouteHandle,
                lazy: () => import('@/modules/projects/pages/create-project-page').then((m) => ({ Component: m.CreateProjectPage })),
              },
              {
                path: `${paths.projects}/:projectId/edit`,
                handle: { title: 'Edit project' } satisfies RouteHandle,
                lazy: () => import('@/modules/projects/pages/edit-project-page').then((m) => ({ Component: m.EditProjectPage })),
              },
              {
                path: `${paths.projects}/:projectId`,
                handle: { title: 'Project' } satisfies RouteHandle,
                lazy: () => import('@/modules/projects/pages/project-page').then((m) => ({ Component: m.ProjectPage })),
              },
              {
                element: <RequireUsersAccess />,
                children: [
                  {
                    path: paths.users,
                    handle: { title: 'Users' } satisfies RouteHandle,
                    lazy: () => import('@/modules/accounts/pages/users-page').then((m) => ({ Component: m.UsersPage })),
                  },
                ],
              },
              {
                path: paths.collections,
                handle: { title: 'Collections' } satisfies RouteHandle,
                lazy: () => import('@/modules/collections/pages/collections-page').then((m) => ({ Component: m.CollectionsPage })),
              },
              {
                path: `${paths.collections}/:collectionId`,
                handle: { title: 'Collection' } satisfies RouteHandle,
                lazy: () => import('@/modules/collections/pages/collection-page').then((m) => ({ Component: m.CollectionPage })),
              },
              {
                path: `${paths.templates}/:projectId`,
                handle: { title: 'Template' } satisfies RouteHandle,
                lazy: () => import('@/modules/projects/pages/template-versions-page').then((m) => ({ Component: m.TemplateVersionsPage })),
              },
              {
                path: paths.notifications,
                handle: { title: 'Notifications' } satisfies RouteHandle,
                lazy: () => import('@/modules/notifications/pages/notifications-page').then((m) => ({ Component: m.NotificationsPage })),
              },
              {
                path: paths.settings,
                handle: { title: 'Settings' } satisfies RouteHandle,
                lazy: () => import('@/modules/settings/pages/settings-page').then((m) => ({ Component: m.SettingsPage })),
              },
            ],
          },
        ],
      },
      { path: '*', Component: HomeRedirect },
    ],
  },
]

export const createAppRouter = () => createBrowserRouter(routes)

/** Same routes in memory, for tests. */
export const createTestRouter = (initialEntries: string[]) => createMemoryRouter(routes, { initialEntries })
